import { useCallback, useEffect, useMemo } from 'react';
import { useDashboardScope } from '@/features/dashboard/components/DashboardScopeContext';
import { useAuthStore, useIsSSI } from '@/stores/auth.store';
import { useAiChatStore } from '@/stores/ai-chat.store';
import {
  aiScopeRequestFor,
  greetingFocusFor,
  isNarrowedLens,
  scopeLabelFor,
  type AiChatLens,
} from '../lib/ai-chat-scope';
import type { ChatMessage } from '../types';

/**
 * TruInsight AI conversation controller.
 *
 * Thin wrapper over the shared `ai-chat.store` (so the dashboard widget and the global FAB are one
 * live conversation) that layers on the React-derived context the store can't read on its own:
 *   • personalization — the greeting uses the caller's first name;
 *   • scope — SSI callers answer across ALL companies, or the active dashboard lens (company /
 *     building / view-as-user). Everyone else is pinned server-side to their own company.
 *     Scope comes from `DashboardScopeProvider` on AppShell (DEF-269) so FAB + widget agree.
 */

/** Build the personalized welcome bubble. Stable `id` so history-skipping + hydration guards work. */
function buildWelcome(firstName: string, isSSI: boolean, lens: AiChatLens): ChatMessage {
  const hi = firstName ? `Hi ${firstName}!` : 'Hi!';
  const focus = greetingFocusFor(isSSI, lens);
  return {
    id: 'welcome',
    role: 'assistant',
    content: `${hi} I'm TruInsight AI, your portfolio intelligence assistant. Ask me anything about ${focus} — buildings, tenants, leases, or market data.\n\nTry questions like:\n- "How much vacant space do I have?"\n- "What leases are expiring in the next 90 days?"\n- "What is the occupancy rate across the portfolio?"`,
    timestamp: Date.now(),
  };
}

/** Suggested starter prompts, tuned to whether the caller sees one company or the whole book. */
function suggestionsFor(isSSI: boolean, isNarrowed: boolean): string[] {
  if (isSSI && !isNarrowed) {
    return [
      'Which companies have the most vacant space?',
      'What leases are expiring in the next 90 days?',
      'Which company has the highest occupancy rate?',
      'How many buildings does each company manage?',
    ];
  }
  return [
    'How much vacant space do I have by market?',
    'What leases are expiring in the next 90 days?',
    'What space is coming up for renewal?',
    'What is the occupancy rate across my portfolio?',
  ];
}

export interface UseAiChat {
  messages: ChatMessage[];
  isLoading: boolean;
  sendMessage: (question: string) => void;
  clearHistory: () => void;
  /** True for SSI staff — gates internal-only affordances (e.g. the raw SQL disclosure). */
  isSSI: boolean;
  /** Human-readable scope shown in the header ("All companies", a company/user name, …). */
  scopeLabel: string;
  /** Role/scope-aware starter prompts. */
  suggestions: string[];
}

export function useAiChat(): UseAiChat {
  const userId = useAuthStore((s) => s.user?.userId) ?? 0;
  const firstName = useAuthStore((s) => s.user?.firstName ?? '');
  const isSSI = useIsSSI();

  const dashScope = useDashboardScope();
  const lens: AiChatLens = useMemo(
    () => ({
      mode: dashScope.mode,
      companyId: dashScope.companyId,
      companyName: dashScope.companyName,
      buildingId: dashScope.buildingId,
      buildingName: dashScope.buildingName,
      scopeAsUserId: dashScope.scopeAsUserId,
      scopeAsUserName: dashScope.scopeAsUserName,
    }),
    [
      dashScope.mode,
      dashScope.companyId,
      dashScope.companyName,
      dashScope.buildingId,
      dashScope.buildingName,
      dashScope.scopeAsUserId,
      dashScope.scopeAsUserName,
    ],
  );
  const scopeRequest = useMemo(() => aiScopeRequestFor(isSSI, lens), [isSSI, lens]);

  const messages = useAiChatStore((s) => s.messages);
  const isLoading = useAiChatStore((s) => s.isLoading);
  const hydrate = useAiChatStore((s) => s.hydrate);
  const replaceWelcome = useAiChatStore((s) => s.replaceWelcome);
  const send = useAiChatStore((s) => s.sendMessage);
  const clear = useAiChatStore((s) => s.clearHistory);

  const welcome = useMemo(
    () => buildWelcome(firstName, isSSI, lens),
    [firstName, isSSI, lens],
  );
  const scopeLabel = useMemo(() => scopeLabelFor(isSSI, lens), [isSSI, lens]);
  const suggestions = useMemo(
    () => suggestionsFor(isSSI, isNarrowedLens(isSSI, lens)),
    [isSSI, lens],
  );

  // Hydrate the shared thread once per user (the store guards against a double fetch when both the
  // widget and the FAB mount). Re-runs harmlessly when `welcome` identity changes — the store no-ops.
  useEffect(() => {
    if (userId > 0) void hydrate(userId, welcome);
  }, [userId, welcome, hydrate]);

  // Keep the welcome bubble + header in sync when the SSI lens changes mid-session (DEF-269).
  // Only rewrites the `id: 'welcome'` row — never wipes an in-progress conversation.
  useEffect(() => {
    replaceWelcome(welcome);
  }, [welcome, replaceWelcome]);

  const sendMessage = useCallback(
    (question: string) => {
      // Non-SSI callers send null overrides; the backend pins them to their JWT company/user.
      void send(question, scopeRequest);
    },
    [send, scopeRequest],
  );

  const clearHistory = useCallback(() => clear(welcome), [clear, welcome]);

  return { messages, isLoading, sendMessage, clearHistory, isSSI, scopeLabel, suggestions };
}
